<template>
    <div
        data-test="status-message"
        v-if="!dismissed"
        @click="dismiss"
        :class="[
            type === 'error' ? 'bg-red-50' : 'bg-emerald-50',
            'rounded-md p-4 cursor-pointer',
        ]"
    >
        <div class="flex">
            <div class="shrink-0">
                <component
                    :is="type === 'error' ? XCircleIcon : CheckCircleIcon"
                    :class="[
                        type === 'error' ? 'text-red-400' : 'text-emerald-400',
                        'h-5 w-5',
                    ]"
                    aria-hidden="true"
                />
            </div>

            <div class="ml-3">
                <a v-if="link" :href="link">
                    <p
                        :class="[
                            type === 'error'
                                ? 'text-red-800'
                                : 'text-emerald-800',
                            'text-sm font-medium',
                        ]"
                    >
                        {{ message }}
                    </p>
                </a>
                <p
                    v-else
                    :class="[
                        type === 'error' ? 'text-red-800' : 'text-emerald-800',
                        'text-sm font-medium',
                    ]"
                >
                    {{ message }}
                </p>
            </div>

            <div class="ml-auto pl-3">
                <div class="-mx-1.5 -my-1.5">
                    <button
                        type="button"
                        :class="[
                            type === 'error'
                                ? 'bg-red-50 text-red-500 hover:bg-red-100'
                                : 'bg-emerald-50 text-emerald-500 hover:bg-emerald-100',
                            'inline-flex rounded-md p-1.5 focus:outline-none',
                        ]"
                    >
                        <span class="sr-only">Dismiss</span>
                        <XIcon class="h-5 w-5" aria-hidden="true" />
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { inject, ref, watch } from "vue";
import { CheckCircleIcon, XCircleIcon, XIcon } from "@heroicons/vue/solid";
import type { AppEmitter } from "../emitter";

const props = withDefaults(
    defineProps<{
        initialMessage?: string;
        initialDismissed?: string;
        link?: string;
        type?: string;
    }>(),
    {
        initialDismissed: "",
    },
);

const emitter = inject<AppEmitter>("mitt")!;

const dismissed = ref(!!props.initialDismissed);
const message = ref<unknown>(props.initialMessage);

function setNewMessage(newMessage: unknown): void {
    dismissed.value = false;
    message.value = newMessage;
}

watch(
    () => props.initialMessage,
    (newMessage) => {
        setNewMessage(newMessage);
    },
);

emitter.on("newStatusMessage", (newMessage) => {
    setNewMessage(newMessage);
});

function dismiss(): void {
    if (props.link) {
        window.location.href = props.link;
    }

    dismissed.value = true;
}
</script>
